import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Contact, CreateContactInput, UpdateContactInput } from '@faktorplus/shared';
import { apiRequest } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

/** The whole book at once — small enough that search happens on the client. */
export function useContacts() {
  return useQuery({
    queryKey: queryKeys.contacts.all,
    queryFn: () => apiRequest<Contact[]>('/contacts'),
  });
}

function useInvalidateContacts() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
}

export function useCreateContact() {
  const invalidate = useInvalidateContacts();

  return useMutation({
    mutationFn: (input: CreateContactInput) =>
      apiRequest<Contact>('/contacts', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateContact() {
  const invalidate = useInvalidateContacts();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContactInput }) =>
      apiRequest<Contact>(`/contacts/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

export function useDeleteContact() {
  const invalidate = useInvalidateContacts();

  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
